import React, { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import { PRODUCT_TYPE, SORT } from '../ProductConstants';
import { TFilters } from 'types/hooks/useTable';
import { MenuSection } from 'types/DesignSystem/Menu';
import Button from 'Components/DesignSystem/Button/Button';
import Menu from 'Components/DesignSystem/Menu/Menu';
import SegmentedControl from 'Components/DesignSystem/SegmentedControl/SegmentedControl';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { useDismissable } from 'Components/DesignSystem/useDismissable';

type MyProps = {
  filters: TFilters,
  setFilters: Dispatch<SetStateAction<TFilters>>
}

/* Inline copies of the delta-sort / ascending / descending svg assets, strokes on
   currentColor so they inherit the Button / Menu-item ink and theme in dark mode —
   an <img src=…> cannot take currentColor. Shapes unchanged. */
const SortDirectionsIcon = () => (
  <svg className="tw-w-[16.56px] tw-h-[15px]" width="15" height="14" viewBox="0 0 15 14" fill="none" aria-hidden="true">
    <path d="M1.64288 3.68844L4.03249 1.29883L6.44808 3.68844" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.03247 1.29883V9.06506" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13.3571 10.2852L10.9675 12.7007L8.55194 10.2852" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.9675 12.7018V4.93555" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AscendingIcon = () => (
  <svg width="7" height="10" viewBox="0 0 7 10" fill="none" aria-hidden="true">
    <path d="M5.8052 6.46875L3.41558 8.88433L1 6.46875" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.41553 8.88342V1.11719" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DescendingIcon = () => (
  <svg width="7" height="10" viewBox="0 0 7 10" fill="none" aria-hidden="true">
    <path d="M0.999981 3.53125L3.38959 1.11567L5.80518 3.53125" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.38965 1.11658L3.38965 8.88281" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The Sort popover, on the DS toolbar grammar since § 65: a `subtle` trigger, a
 * `Menu` of two radio sections (direction, field) and a `footer` carrying the
 * Product-type `SegmentedControl` — the segmented switch is not a menu item, so
 * it lives beside the `role="menu"`, not inside it.
 *
 * Two deliberate changes from the legacy markup (okf/products/e2e.md § 4.2):
 * the trigger now TOGGLES (it used to only open — the documented inconsistency
 * against its sibling menus is gone), and every row is keyboard-reachable
 * (the old TextSelector divs had no tabindex at all).
 */
const ProductSort: FC<MyProps> = ({ filters, setFilters }) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);

  /* Caller-level dismissal spanning trigger + menu — see BulkAction for why
     Menu-level `onDismiss` would fight the toggle. */
  const wrapperRef = useRef<HTMLDivElement>(null);
  useDismissable({
    isOpen: showMenu,
    onDismiss: () => setShowMenu(false),
    isInside: (target) => !!wrapperRef.current?.contains(target),
  });

  const sortType = filters?.sort_type ?? 'ASC';
  const sortBy = filters?.sort_by ?? 'name';
  const productType = filters?.product_type ?? 'all';

  const setSortType = (value: string) => () =>
    setFilters((prev) => ({ ...prev, sort_type: value }));
  const setSortBy = (value: string) => () =>
    setFilters((prev) => ({ ...prev, sort_by: value }));

  const sections: MenuSection[] = [
    {
      items: [
        { id: 'asc', label: 'Ascending', icon: <AscendingIcon />, selected: sortType === 'ASC', 'data-testid': 'products-sort-direction-asc', onClick: setSortType('ASC') },
        { id: 'desc', label: 'Descending', icon: <DescendingIcon />, selected: sortType === 'DESC', 'data-testid': 'products-sort-direction-desc', onClick: setSortType('DESC') },
      ],
    },
    {
      items: [
        { id: 'name', label: 'Name', selected: sortBy === 'name', 'data-testid': 'products-sort-by-name', onClick: setSortBy('name') },
        { id: 'created_at', label: 'Date Created', selected: sortBy === 'created_at', 'data-testid': 'products-sort-by-created', onClick: setSortBy('created_at') },
        { id: 'updated_at', label: 'Date Modified', selected: sortBy === 'updated_at', 'data-testid': 'products-sort-by-modified', onClick: setSortBy('updated_at') },
      ],
    },
  ];

  return (
    <div ref={wrapperRef} data-testid="productlist-productsort-div-1" className="tw-relative tw-ms-3">
      <Button
        variant="subtle"
        id="products-sort-btn"
        data-testid="products-sort-btn"
        aria-haspopup="menu"
        aria-expanded={showMenu}
        showIcon
        icon={<SortDirectionsIcon />}
        onClick={() => setShowMenu((prev) => !prev)}
      >
        {SORT}
      </Button>
      {showMenu && (
        <Menu
          sections={sections}
          variant="compact"
          data-testid="products-sort-menu"
          className="tw-absolute tw-left-0 tw-top-full tw-mt-1 tw-w-[260px] tw-z-10 tw-shadow-ds-md"
          footer={
            <div>
              <Typography variant="sub-title-label" className="tw-block tw-px-1 tw-pb-1 tw-text-ink-tertiary">
                {PRODUCT_TYPE}
              </Typography>
              <SegmentedControl
                ariaLabel={PRODUCT_TYPE}
                data-testid="products-sort-product-type"
                options={[
                  { id: 'all', label: 'All' },
                  { id: 'parent', label: 'Parent/Child' },
                  { id: 'individual', label: 'Individual' },
                ]}
                value={productType}
                onChange={(id) =>
                  setFilters((prev) => ({
                    ...prev,
                    product_type: id === 'all' ? undefined : (id as string),
                  }))
                }
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export default ProductSort;
